// Edge schemas
// ADR-0007: Hybrid File Organization - Edges in one file (< 5 types, < 200 lines)

import { z } from 'zod';
import { BaseEdgeSchema } from '../base/edge';
import { ValueWithUnitSchema } from '../base/common';

// ============================================================================
// Pipe Fittings (shared by pipe and compressible_pipe)
// ============================================================================

export const PipeFittingSchema = z.object({
  /** References a Crane catalog entry ID, or "custom" for manual K entry */
  fittingId: z.string(),
  /** Number of this fitting on the pipe */
  count: z.number().int().positive(),
  /** Manual K value — only used when fittingId === "custom" */
  customK: z.number().optional(),
  /** Valve bore or reducer diameter in meters — for formulas 1, 3, 5, 7 */
  reducedDiameter: z.number().optional(),
  /** Bend angle in degrees — for formula 8 bends (default: 90) */
  angle: z.number().optional(),
});

export type PipeFitting = z.infer<typeof PipeFittingSchema>;

// ============================================================================
// Pipe Edge (existing + v2 additions)
// ============================================================================

export const PipeEdgeSchema = BaseEdgeSchema.extend({
  type: z.literal('pipe'),
  fluid_zone_id: z.string(),
  pipe_specification_id: z.string(),
  pipe_size_id: z.string(),
  length: ValueWithUnitSchema,
  is_closed: z.boolean(),
  is_oneway: z.boolean(),
  fittings: z.array(PipeFittingSchema).optional(),

  heat_transfer_settings_id: z.string().optional(),
  design_factor: z.number().optional(),
  valve_closure_time: ValueWithUnitSchema.optional(),
  wall_thickness: ValueWithUnitSchema.optional(),
  outer_diameter: ValueWithUnitSchema.optional(),
  flow_area: ValueWithUnitSchema.optional(),
});

export type PipeEdge = z.infer<typeof PipeEdgeSchema>;

// ============================================================================
// Compressible Pipe Edge (NEW in v2)
// C++ compressible_pipe_design is typedef of pipe_design — identical fields
// ============================================================================

export const CompressiblePipeEdgeSchema = BaseEdgeSchema.extend({
  type: z.literal('compressible_pipe'),
  fluid_zone_id: z.string(),
  pipe_specification_id: z.string(),
  pipe_size_id: z.string(),
  length: ValueWithUnitSchema,
  is_closed: z.boolean(),
  is_oneway: z.boolean(),
  fittings: z.array(PipeFittingSchema).optional(),

  heat_transfer_settings_id: z.string().optional(),
  design_factor: z.number().optional(),
  valve_closure_time: ValueWithUnitSchema.optional(),
  wall_thickness: ValueWithUnitSchema.optional(),
  outer_diameter: ValueWithUnitSchema.optional(),
  flow_area: ValueWithUnitSchema.optional(),
});

export type CompressiblePipeEdge = z.infer<typeof CompressiblePipeEdgeSchema>;

// ============================================================================
// Duct Edge (NEW in v2)
// Different design fields from pipe — no pipe specification, has own geometry
// ============================================================================

const DuctGeometryCircularSchema = z.object({
  shape: z.literal('circular'),
  diameter: ValueWithUnitSchema,
});

const DuctGeometryRectangularSchema = z.object({
  shape: z.literal('rectangular'),
  width: ValueWithUnitSchema,
  height: ValueWithUnitSchema,
});

export const DuctGeometrySchema = z.discriminatedUnion('shape', [
  DuctGeometryCircularSchema,
  DuctGeometryRectangularSchema,
]);

export type DuctGeometry = z.infer<typeof DuctGeometrySchema>;

export const DuctEdgeSchema = BaseEdgeSchema.extend({
  type: z.literal('duct'),
  // C++ duct_design::as_json emits these only when is_set() (per as_json audit § 4):
  // a duct authored without an assigned fluid zone, length, or loss coefficient
  // simply omits the field. Connect must accept absence as a real signal.
  fluid_zone_id: z.string().optional(),
  length: ValueWithUnitSchema.optional(),
  geometry: DuctGeometrySchema,
  roughness: ValueWithUnitSchema.optional(),
  /** Overall loss coefficient K (fittings + transitions) */
  loss_coefficient: z.number().optional(),
  design_factor: z.number().optional(),
  is_closed: z.boolean(),
  flow_area: ValueWithUnitSchema.optional(),
  hydraulic_diameter: ValueWithUnitSchema.optional(),
});

export type DuctEdge = z.infer<typeof DuctEdgeSchema>;

// ============================================================================
// Edge Union (3-type discriminated union)
// ============================================================================

export const EdgeSchema = z.discriminatedUnion('type', [
  PipeEdgeSchema,
  CompressiblePipeEdgeSchema,
  DuctEdgeSchema,
]);

export type Edge = z.infer<typeof EdgeSchema>;
